import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';

import { useHarvestStore } from '@/stores/harvest-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { useHuntStore } from '@/stores/hunt-store';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/common/Button';
import { InlineErrorBanner } from '@/components/common/InlineErrorBanner';
import { PhotoPicker } from '@/components/photos/PhotoPicker';
import { PhotoGallery } from '@/components/photos/PhotoGallery';
import { ThumbnailPickerSheet } from '@/components/hunts/ThumbnailPickerSheet';
import { DateField } from '@/components/forms/DateField';
import {
  getPhotosForHarvest,
  addPhotosToHarvest,
  deleteHarvestPhoto,
  setHarvestThumbnail,
} from '@/services/photo-service';
import type { HarvestSex } from '@/types/hunt';
import type { PhotoRecord } from '@/types/photo';
import { localTodayISO } from '@/utils/date-utils';

// M-08 (Defect Batch M): switched from `focus:ring-offset-2` to a negative
// outline-offset so the focus ring renders inside the input's bounding box
// and is never clipped by an ancestor's overflow:hidden (ADR-035).
// Pending iOS Safari PWA dogfood verification (ADR-035, RULE-194–199).
const INPUT_CLASS =
  'w-full min-w-0 box-border px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-placeholder)] focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px]';

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="field-label"
    >
      {children}
      {required && <span className="text-[var(--color-accent)] ml-1">*</span>}
    </label>
  );
}

function FieldWrapper({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>;
}

const SPECIES_OPTIONS = [
  'Whitetail Deer',
  'Turkey',
  'Duck',
  'Goose',
  'Pheasant',
  'Bear',
  'Elk',
  'Other',
];

const todayISO = localTodayISO();

export function HarvestFormPage() {
  const { huntId, harvestId } = useParams<{ huntId: string; harvestId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { records, createRecord, updateRecord, loadForHunt } = useHarvestStore();
  const { waypoints: locations, loadWaypoints: loadLocations } = useWaypointStore();
  const { currentHunt, loadHunt } = useHuntStore();
  const userId = useAuthStore((s) => s.user?.id);

  const isEdit = Boolean(harvestId);
  const sitId = searchParams.get('sitId') ?? undefined;

  const [species, setSpecies] = useState('Whitetail Deer');
  const [sex, setSex] = useState<HarvestSex>('buck');
  const [date, setDate] = useState(todayISO);
  const [waypointId, setWaypointId] = useState('');
  const [weight, setWeight] = useState('');
  const [antlerPoints, setAntlerPoints] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Create mode: files collected before record exists
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // Edit mode: photos already attached to the record
  const [existingPhotos, setExistingPhotos] = useState<PhotoRecord[]>([]);
  // Thumbnail picker sheet
  const [thumbnailPickerOpen, setThumbnailPickerOpen] = useState(false);
  // Photo attachment error — surfaced when photo attach fails post-create
  const [photoAttachError, setPhotoAttachError] = useState<string | null>(null);
  // Auth / general save error — surfaced when userId is null (ADR-033) or save throws
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load hunt + locations on mount
  useEffect(() => {
    if (huntId) {
      void loadHunt(huntId);
      void loadForHunt(huntId);
    }
  }, [huntId, loadHunt, loadForHunt]);

  useEffect(() => {
    if (currentHunt?.propertyId) {
      void loadLocations(currentHunt.propertyId);
    }
  }, [currentHunt?.propertyId, loadLocations]);

  // Pre-fill from sit's location
  useEffect(() => {
    if (sitId && !isEdit) {
      // locationId will be set when sits are available; handled via sit-based nav
      // For now we rely on the user selecting location from the sit's property
    }
  }, [sitId, isEdit]);

  // Load existing photos in edit mode
  useEffect(() => {
    if (!isEdit || !harvestId) return;
    getPhotosForHarvest(harvestId)
      .then(setExistingPhotos)
      .catch((err) => console.error('[HarvestFormPage] Failed to load photos:', err));
  }, [isEdit, harvestId]);

  // Pre-fill for edit mode
  useEffect(() => {
    if (!isEdit || !harvestId) return;
    const record = records.find((r) => r.id === harvestId);
    if (!record) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
    setSpecies(record.species);
    setSex(record.sex);
    setDate(record.date);
    setWaypointId(record.waypointId);
    setWeight(record.weight !== undefined ? String(record.weight) : '');
    setAntlerPoints(record.antlerPoints !== undefined ? String(record.antlerPoints) : '');
    setNotes(record.notes ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [harvestId, records.length]);

  const handleDeleteExistingPhoto = useCallback(
    async (photoId: string) => {
      if (!harvestId) return;
      try {
        await deleteHarvestPhoto(photoId, harvestId);
        setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));
        const record = records.find((r) => r.id === harvestId);
        if (record?.thumbnailPhotoId === photoId) {
          await updateRecord(harvestId, { thumbnailPhotoId: undefined });
        }
      } catch (err) {
        console.error('[HarvestFormPage] Failed to delete photo:', err);
      }
    },
    [harvestId, records, updateRecord],
  );

  const handleThumbnailPickerSelect = useCallback(
    async (photoId: string | null) => {
      if (!harvestId) return;
      try {
        await setHarvestThumbnail(harvestId, photoId);
        await updateRecord(harvestId, { thumbnailPhotoId: photoId ?? undefined });
      } catch (err) {
        console.error('[HarvestFormPage] Failed to set thumbnail via picker:', err);
      } finally {
        setThumbnailPickerOpen(false);
      }
    },
    [harvestId, updateRecord],
  );

  const showAntlerPoints = sex === 'buck' && species === 'Whitetail Deer';

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!species) errs['species'] = 'Species is required';
    if (!date) errs['date'] = 'Date is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || !huntId) return;
    setIsSaving(true);
    setSubmitError(null);
    try {
      // ADR-033: throw if user is not authenticated — never substitute a placeholder.
      if (!userId) {
        throw new Error('HarvestFormPage: cannot save — user not authenticated');
      }
      if (isEdit && harvestId) {
        await updateRecord(harvestId, {
          species,
          sex,
          date,
          waypointId,
          weight: weight ? Number(weight) : undefined,
          antlerPoints: showAntlerPoints && antlerPoints ? Number(antlerPoints) : undefined,
          notes: notes.trim() || undefined,
        });
        navigate(`/hunt/${huntId}`);
      } else {
        const record = await createRecord({
          huntId,
          huntSitId: sitId,
          waypointId,
          date,
          species,
          sex,
          weight: weight ? Number(weight) : undefined,
          antlerPoints: showAntlerPoints && antlerPoints ? Number(antlerPoints) : undefined,
          notes: notes.trim() || undefined,
          photos: [],
        });
        // Attach pending photos after record is created
        if (pendingFiles.length > 0) {
          try {
            await addPhotosToHarvest(record.id, userId, pendingFiles);
          } catch (err) {
            console.error('[HarvestFormPage] Failed to attach photos:', err);
            const message = err instanceof Error ? err.message : 'Photos could not be attached. Please try again.';
            setPhotoAttachError(message);
            setIsSaving(false);
            return;
          }
        }
        setPhotoAttachError(null);
        navigate(`/hunt/${huntId}/harvest/${record.id}/celebrate`);
      }
    } catch (err) {
      console.error('[HarvestFormPage] save failed:', err);
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 overflow-hidden" data-testid="harvest-form-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={`/hunt/${huntId ?? ''}`}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          {isEdit ? 'Edit Harvest' : 'Log Harvest'}
        </h1>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 overflow-hidden"
        data-testid="harvest-form"
      >
        {/* Species */}
        <FieldWrapper>
          <FieldLabel htmlFor="harvest-species" required>
            Species
          </FieldLabel>
          <select
            id="harvest-species"
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            className={INPUT_CLASS}
            data-testid="select-species"
          >
            {SPECIES_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {errors['species'] && (
            <p className="text-body-xs text-[var(--color-accent)]">{errors['species']}</p>
          )}
        </FieldWrapper>

        {/* Sex */}
        <FieldWrapper>
          <FieldLabel>Sex</FieldLabel>
          <div className="grid grid-cols-3 gap-1 bg-[var(--color-elevated)] p-1 rounded-md">
            {(['buck', 'doe', 'unknown'] as HarvestSex[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSex(s)}
                className={`py-1.5 rounded text-body-xs font-medium transition-colors capitalize ${
                  sex === s
                    ? 'bg-[var(--color-surface)] text-[var(--color-text-primary)] shadow-sm border border-[var(--color-border)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                }`}
                data-testid={`sex-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </FieldWrapper>

        {/* Date */}
        <FieldWrapper>
          <DateField
            id="harvest-date"
            label="Date"
            value={date}
            onChange={setDate}
            required
          />
          {errors['date'] && (
            <p className="text-body-xs text-[var(--color-accent)]">{errors['date']}</p>
          )}
        </FieldWrapper>

        {/* Location */}
        <FieldWrapper>
          <FieldLabel htmlFor="harvest-location">Location</FieldLabel>
          <select
            id="harvest-location"
            value={waypointId}
            onChange={(e) => setWaypointId(e.target.value)}
            className={INPUT_CLASS}
            data-testid="select-location"
          >
            <option value="">— Select location (optional) —</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </FieldWrapper>

        {/* Weight */}
        <FieldWrapper>
          <FieldLabel htmlFor="harvest-weight">Weight (lbs)</FieldLabel>
          <input
            id="harvest-weight"
            type="number"
            min="0"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="Optional"
            className={INPUT_CLASS}
            data-testid="input-weight"
          />
        </FieldWrapper>

        {/* Antler Points — only for buck + whitetail */}
        {showAntlerPoints && (
          <FieldWrapper>
            <FieldLabel htmlFor="harvest-antler-points">Antler Points (total)</FieldLabel>
            <input
              id="harvest-antler-points"
              type="number"
              min="0"
              step="1"
              value={antlerPoints}
              onChange={(e) => setAntlerPoints(e.target.value)}
              placeholder="e.g. 8"
              className={INPUT_CLASS}
              data-testid="input-antler-points"
            />
          </FieldWrapper>
        )}

        {/* Notes */}
        <FieldWrapper>
          <FieldLabel htmlFor="harvest-notes">Notes</FieldLabel>
          <textarea
            id="harvest-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Shot distance, conditions, story…"
            className={`${INPUT_CLASS} resize-y`}
            data-testid="input-notes"
          />
        </FieldWrapper>

        {/* Photos */}
        <div className="flex flex-col gap-2" data-testid="harvest-photos-section">
          <p className="field-label">
            {isEdit
              ? `PHOTOS (${existingPhotos.length}/10)`
              : `PHOTOS${pendingFiles.length > 0 ? ` (${pendingFiles.length} selected)` : ''}`}
          </p>

          {/* Edit mode: show gallery of existing photos + single "Choose Thumbnail" button */}
          {isEdit && existingPhotos.length > 0 && (
            <div className="flex flex-col gap-2">
              <PhotoGallery
                photos={existingPhotos}
                onDelete={(photoId) => void handleDeleteExistingPhoto(photoId)}
                editable
              />
              <button
                type="button"
                onClick={() => setThumbnailPickerOpen(true)}
                className="self-start text-body-xs px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-elevated)] text-[var(--color-text-muted)] hover:border-[var(--color-accent)] transition-colors"
                data-testid="open-thumbnail-picker"
              >
                Choose Thumbnail
              </button>
            </div>
          )}

          {/* Create mode: show count of pending files */}
          {!isEdit && pendingFiles.length > 0 && (
            <p className="text-body-xs text-[var(--color-text-muted)]" data-testid="pending-photos-count">
              {pendingFiles.length} photo{pendingFiles.length !== 1 ? 's' : ''} selected
            </p>
          )}

          <PhotoPicker
            entityType="harvest"
            entityId={harvestId ?? ''}
            existingCount={isEdit ? existingPhotos.length : pendingFiles.length}
            onPhotosAdded={(newPhotos) =>
              setExistingPhotos((prev) => [...prev, ...newPhotos])
            }
            onAddFiles={!isEdit ? (files) => setPendingFiles((prev) => [...prev, ...files]) : undefined}
          />
        </div>

        {/* Photo attachment error banner */}
        {photoAttachError !== null && (
          <InlineErrorBanner
            message={photoAttachError}
            data-testid="photo-attach-error"
          />
        )}

        {/* Auth / save error banner (e.g. user not authenticated — ADR-033) */}
        {submitError !== null && (
          <InlineErrorBanner
            message={submitError}
            data-testid="submit-error"
          />
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-2">
          <Button
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={() => navigate(`/hunt/${huntId ?? ''}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSaving}
            className="flex-1"
            data-testid="submit-button"
          >
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Log Harvest'}
          </Button>
        </div>
      </form>

      {/* Thumbnail picker sheet — edit mode only, opens when "Choose Thumbnail" is tapped */}
      {isEdit && (
        <ThumbnailPickerSheet
          isOpen={thumbnailPickerOpen}
          photos={existingPhotos}
          currentThumbnailId={records.find((r) => r.id === harvestId)?.thumbnailPhotoId}
          onSelect={(photoId) => void handleThumbnailPickerSelect(photoId)}
          onClose={() => setThumbnailPickerOpen(false)}
        />
      )}
    </div>
  );
}
